import type { ReactNode } from "react";
import Image from "next/image";
import classNames from "classnames";
import { TextButton } from "components/textButton/TextButton";
import { TRANSPARENT_PIXEL } from "lib/constants/shared";
import { createCloudflareLoader } from "utils";

type ProCardBaseProps = {
  headline: string;
  description: ReactNode;
  /** Heading level for the headline; h3 by default. */
  headingLevel?: "h3" | "h4" | "h5";
  className?: string;
  "data-testid"?: string;
};

/** The homepage card: image and "See …" link. */
type ProCardLinkProps = ProCardBaseProps & {
  variant?: "link";
  image?: {
    url: string;
    alt?: string | null;
    width?: number;
    height?: number;
    blurUpThumb?: string;
  } | null;
  buttonText: string;
  buttonUrl: string;
  onButtonClick?: () => void;
};

/**
 * Information only: title and body on plain white, with no glow, image or
 * link, so it never reads as clickable. An optional decorative icon sits
 * above the title.
 */
type ProCardInfoProps = ProCardBaseProps & {
  variant: "info";
  /** Decorative; rendered inside an aria-hidden wrapper. */
  icon?: ReactNode;
};

export type ProCardProps = ProCardLinkProps | ProCardInfoProps;

const GLOW =
  "bg-[radial-gradient(50%_50%_at_50%_50%,rgba(189,189,255,0.95)_0%,rgba(244,244,246,0.95)_100%)]";

const proCardImageLoader = createCloudflareLoader({
  aspectRatio: "2:1",
  fit: "cover",
  format: "auto",
});

const ProCard: React.FC<ProCardProps> = (props) => {
  const {
    headline,
    description,
    headingLevel: Heading = "h3",
    className,
    "data-testid": dataTestId,
  } = props;

  if (props.variant === "info") {
    return (
      <div
        className={classNames(
          "flex cursor-default flex-col items-start gap-3 bg-mono-00 p-4",
          className
        )}
        data-testid={dataTestId}
      >
        {props.icon && (
          <div aria-hidden="true" className="shrink-0">
            {props.icon}
          </div>
        )}
        <div className="flex w-full flex-col gap-[14px]">
          <Heading className="font-recklessBold text-accent-18 text-brand-neutral">
            {headline}
          </Heading>
          <p className="text-regular-16 text-brand-neutral">{description}</p>
        </div>
      </div>
    );
  }

  const { image, buttonText, buttonUrl, onButtonClick } = props;
  const imageAlt = image?.alt || headline;

  return (
    <div
      className={classNames(
        "flex flex-col items-start justify-between gap-4 p-4",
        GLOW,
        className
      )}
      data-testid={dataTestId}
    >
      <div className="flex w-full flex-col gap-[14px]">
        <Heading className="font-recklessBold text-accent-18 text-brand-neutral">
          {headline}
        </Heading>
        <p className="text-regular-16 text-brand-neutral">{description}</p>
      </div>
      <div className="flex w-full flex-col gap-4">
        {image && (
          <div className="relative aspect-[2/1] w-full overflow-hidden rounded-lg">
            <Image
              loader={proCardImageLoader}
              src={image.url}
              alt={imageAlt}
              fill
              className="object-cover"
              sizes="(max-width: 640px) calc(100vw - 40px), (max-width: 768px) calc(50vw - 20px), (max-width: 1024px) calc(33.33vw - 16px), calc(25vw - 8px)"
              blurDataURL={image.blurUpThumb || TRANSPARENT_PIXEL}
              placeholder="blur"
            />
          </div>
        )}

        <TextButton
          label={buttonText}
          href={buttonUrl}
          onClick={onButtonClick}
          variant="neutral"
          iconPosition="right"
        />
      </div>
    </div>
  );
};

export default ProCard;
